import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Grid,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DynamicDeskLogo from "../../assets/DD_LOGO_SCHWARZ.png";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../stores/zustand";

const VerifyComponent = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const code = searchParams.get("code");
  const navigate = useNavigate();
  const setAuthData = useAuthStore((state) => state.setAuthData);
  const [error, setError] = useState(null);

  const [dialogMessage, setDialogMessage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  /**
   * Listen for language changes from Electron menu
   */
  useEffect(() => {
    if (window.electronAPI?.language) {
      const cleanup = window.electronAPI.language.onChange((newLanguage) => {
        i18n.changeLanguage(newLanguage);
      });
      return cleanup; // Cleanup on unmount
    }
  }, [i18n]);

  const handleEmailVerificationClose = () => {
    useAuthStore.getState().clearAuth();
    navigate("/");
  };

  const closeSuccessDialog = () => {
    setDialogOpen(false);
    setDialogMessage(null);

    const currentProduct = useAuthStore.getState().currentProduct;

    useAuthStore.getState().clearAuth();

    if (currentProduct === "physio_therapist") {
      navigate("/physio");
    } else if (currentProduct === "physio_user") {
      navigate("/physio-user");
    } else if (currentProduct === "corporate") {
      navigate("/corporate");
    } else {
      // Fallback to landing page if product unknown
      navigate("/");
    }
  };

  useEffect(() => {
    if (code === null) {
      setError(t("noVerificationCodeFound"));
      return;
    }

    // helper to show success dialog after language is set
    const showSuccessDialog = () => {
      const msg =
        t("emailVerifiedSuccess") ||
        "Your email has been verified. Please login.";
      setDialogMessage(msg);
      setDialogOpen(true);
    };

    fetch(`${process.env.REACT_APP_FIREBASE_API_HOST}/api/auth/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(t("errorVerifyingEmail"));
        }
        return response.json();
      })
      .then((data) => {
        if (!data || !data.auth) {
          throw new Error(t("codeAlreadyUsed"));
        }

        if (data.auth && data.auth.current_product) {
          try {
            setAuthData({
              name: data.auth.name || "",
              email: data.auth.email || "",
              isPaid: data.auth.isPaid || false,
              isEmailVerified: data.auth.isEmailVerified || true,
              current_product: data.auth.current_product,
              subscribed_products: data.subscribed_products || [],
              preferredLanguage: data.auth.preferredLanguage || "de",
              onboarding_completed: data.auth.onboarding_completed || false,
            });
          } catch (e) {
            console.debug(e);
          }
        }

        // Use preferredLanguage from backend
        const userLang = data.auth?.preferredLanguage || "de";

        // If language already matches, just show dialog; else change and then show
        if (i18n.language === userLang) {
          showSuccessDialog();
        } else {
          i18n
            .changeLanguage(userLang)
            .then(() => {
              showSuccessDialog();
            })
            .catch((err) => {
              console.warn(
                "changeLanguage failed, showing dialog with current language",
                err,
              );
              showSuccessDialog();
            });
        }
      })
      .catch((err) => {
        console.error("Verification error:", err);
        setError(err.message || t("errorVerifyingEmail"));
      });
  }, [code, i18n, t, setAuthData]);

  return (
    <Container maxWidth="lg" sx={{ height: "80vh", marginTop: "120px" }}>
      {/* Error Dialog */}
      {error && (
        <Dialog open={true} onClose={handleEmailVerificationClose}>
          <DialogTitle>{t("error")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {typeof error === "string"
                ? error
                : error?.message || t("errorVerifyingEmail")}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEmailVerificationClose}>{t("close")}</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Success Dialog */}
      {dialogMessage && (
        <Dialog open={dialogOpen} onClose={closeSuccessDialog}>
          <DialogTitle>{t("emailVerified")}</DialogTitle>
          <DialogContent>
            <DialogContentText>{dialogMessage}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeSuccessDialog} autoFocus>
              {t("proceedToLogin")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <div
        className="white-background"
        style={{
          width: "100%",
          height: "82%",
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            padding: 3,
            height: "100%",
            justifyContent: "center",
            alignContent: "center",
            overflowY: "auto",
          }}
        >
          {/* Left column for icon and text */}
          <Grid item xs={12} md={5}>
            <img
              src={DynamicDeskLogo}
              style={{ width: "124px", height: "124px", marginLeft: "0px" }}
              alt="Feedback"
            />
            <Typography
              variant="h4"
              style={{ marginTop: "42px", color: "#063bd0" }}
              gutterBottom
            >
              <b>{t("almostThere")}</b>
            </Typography>
            <Typography
              variant="h6"
              style={{ marginTop: "18px", color: "#063bd0" }}
            >
              {t("busyFinalizingPleaseWait")}
            </Typography>
          </Grid>

          {/* Divider */}
          <Grid item xs={12} md={1}>
            <Divider
              orientation="vertical"
              sx={{ width: "2px", height: "100%", bgcolor: "divider" }}
            />
          </Grid>

          {/* Right column for the form */}
          <Grid item xs={12} md={6}>
            <Typography variant="h5" style={{ color: "#063bd0" }} gutterBottom>
              <b>{t("verifying")}</b>
            </Typography>

            <Grid container>
              <Grid
                item
                xs={12}
                md={6}
                display="flex"
                justifyContent="center"
                alignItems="center"
                style={{
                  width: "200px",
                  height: "200px",
                  marginTop: "18px",
                  marginLeft: "100px",
                }}
              >
                <CircularProgress size={75} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Divider
          orientation="vertical"
          flexItem
          style={{ width: "2px", marginRight: "-2px" }}
        />
      </div>

      {/* Success Dialog is intentionally not duplicated here; handled above */}
    </Container>
  );
};

export default VerifyComponent;
